import {
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { CloseIcon, Text } from '@cash-me/react-components'

import useClickOutside from 'hooks/useClickOutside'
import { useScrollLock } from 'hooks/useScrollLock'

import { ModalProps, ModalRefsProps } from './modal.model'
import * as S from './styles'

const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[contenteditable="true"]:not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled]):not([type="hidden"])'
].join(', ')

export const Modal = forwardRef<ModalRefsProps, ModalProps>(({ size = 'medium', title, children }, ref) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const [lockScroll, unlockScroll] = useScrollLock()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const titleId = useId()

  const handleClose = useCallback(() => {
    const previouslyFocusedElement = previouslyFocusedRef.current
    previouslyFocusedRef.current = null

    unlockScroll()
    setIsOpen(false)
    previouslyFocusedElement?.focus()
  }, [unlockScroll])

  useImperativeHandle(
    ref,
    () => ({
      openModal() {
        previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        setIsOpen(true)
        lockScroll()
      },
      closeModal: handleClose
    }),
    [handleClose, lockScroll]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
        return
      }

      if (event.key !== 'Tab') return

      const focusableElements = contentRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (!focusableElements?.length) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    },
    [handleClose]
  )

  const handleClickOutside = useCallback(() => {
    if (isOpen) handleClose()
  }, [handleClose, isOpen])

  useClickOutside(contentRef, handleClickOutside)

  useEffect(() => {
    if (isOpen) closeButtonRef.current?.focus()
  }, [isOpen])

  useEffect(() => () => unlockScroll(), [unlockScroll])

  return (
    <S.Background open={isOpen} aria-hidden={!isOpen}>
      <S.Content
        ref={contentRef}
        size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
      >
        <S.CloseModal ref={closeButtonRef} type="button" onClick={handleClose} aria-label="Fechar modal">
          <CloseIcon aria-hidden color="var(--colors-neutral-icon-default)" />
        </S.CloseModal>

        <S.Header>
          <Text id={titleId} as="h2" size="heading4" weight="medium">
            {title}
          </Text>
        </S.Header>
        <S.InnerContent>{children}</S.InnerContent>
      </S.Content>
    </S.Background>
  )
})

Modal.displayName = 'Modal'
